import { isError } from '../../utils/api'
import debounce from 'lodash.debounce'
import { addLanguageResource, createResource, deleteResource, editResource, getResource } from '../../services/KreaResource'
import { languagesCodesToString } from '../../constants/languages'
import { associateResourceToEducationalContent } from '../../services/KreaEducationalContent'
import { defaultResourceClass } from '../../utils/skeletonResource'
import { deepCopy } from '../../utils/object'
import { suggestTopic } from '../../services/TopicService'
import { classNecessaryMaterialsTypes } from '../../utils/constants'

export default {
  state: {
    isSaving: false,
    savingError: false,
    resource: null,
    activeLanguage: null
  },
  mutations: {
    hydrateKreaResource(state, res) {
      const resource = deepCopy(res)
      delete resource.subtopics
      state.resource = resource
      state.activeLanguage = resource.locales[0]
    },
    clearKreaResource(state) {
      state.resource = {}
      state.activeLanguage = null
    },
    setKreaActiveResourceLanguage(state, locale) {
      state.activeLanguage = locale
    },
    editKreaLocalizedResourceField(state, { field, value, locale }) {
      state.resource.localizedResources = state.resource.localizedResources.map(res => {
        return languagesCodesToString[res.locale] === locale ? ({ ...res, [field]: value }) : res
      })
    },
    editKreaResourceField(state, { field, value }) {
      state.resource[field] = value
    },
    editKreaCustomResourceField(state, { fieldId, newOptions }) {
      if (Array.isArray(state.resource.customKreaFieldOptions)) {
        state.resource.customKreaFieldOptions = [
          ...state.resource.customKreaFieldOptions.filter(op => op.customKreaFieldId !== fieldId),
          ...newOptions
        ]
      } else {
        state.resource.customKreaFieldOptions = newOptions
      }
    },
    editKreaResourceClassField(state, { field, value, locale, classNumber }) {
      const newClasses = state.resource.localizedResources
        .find(res => languagesCodesToString[res.locale] === locale)
        .classPlans.map(resClass => resClass.classNumber === classNumber ? ({ ...resClass, [field]: value }) : resClass)
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? ({ ...res, classPlans: newClasses })
            : res
        })
      }
    },
    addKreaResourceClassMaterial(state, { newMaterial, classNumber, locale }) {
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? {
              ...res,
              classPlans: res.classPlans.map(resClass =>
                resClass.classNumber === classNumber ? { ...resClass, necessaryMaterials: [...resClass.necessaryMaterials, newMaterial] } : resClass)
            }
            : res
        })
      }
    },
    removeKreaResourceClassMaterial(state, { materialId, classNumber, locale }) {
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? { ...res,
              classPlans: res.classPlans.map(resClass =>
                resClass.classNumber === classNumber
                  ? { ...resClass, necessaryMaterials: resClass.necessaryMaterials.filter(mat => mat.id ? mat.id !== materialId : mat.temporalId !== materialId) }
                  : resClass
              )
            }
            : res
        })
      }
    },
    addKreaResourceValuationMaterial(state, { newMaterial, locale }) {
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? { ...res, globalValuationMaterials: [...res.globalValuationMaterials, newMaterial] }
            : res
        })
      }
    },
    removeKreaResourceValuationMaterial(state, { materialId, locale }) {
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? { ...res, globalValuationMaterials: res.globalValuationMaterials.filter(mat => mat.id ? mat.id !== materialId : mat.temporalId !== materialId) }
            : res
        })
      }
    },
    addKreaResourceClass(state, locale) {
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? ({ ...res, classPlans: [...res.classPlans, { ...defaultResourceClass, classNumber: res.classPlans.length + 1 }] })
            : res
        })
      }
    },
    removeKreaResourceClass(state, { locale, classNumber }) {
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? ({
              ...res,
              classPlans: res.classPlans
                .filter(resClass => resClass.classNumber !== classNumber)
                .map(resClass => resClass.classNumber > classNumber ? ({ ...resClass, classNumber: resClass.classNumber - 1 }) : resClass)
            })
            : res
        })
      }
    },
    clearKreaStudentClass(state, { locale, classNumber }) {
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? ({
              ...res,
              classPlans: res.classPlans
                .map(resClass =>
                  resClass.classNumber === classNumber
                    ? ({
                      ...resClass,
                      studentStatement: '',
                      necessaryMaterials: resClass.necessaryMaterials.filter(mat => mat.necessaryMaterialType !== classNecessaryMaterialsTypes.student)
                    })
                    : resClass
                )
            })
            : res
        })
      }
    },
    clearKreaTeacherClass(state, { locale, classNumber }) {
      state.resource = {
        ...state.resource,
        localizedResources: state.resource.localizedResources.map(res => {
          return languagesCodesToString[res.locale] === locale
            ? ({
              ...res,
              classPlans: res.classPlans
                .map(resClass =>
                  resClass.classNumber === classNumber
                    ? ({
                      ...resClass,
                      classLayout: null,
                      organizationType: null,
                      secondaryOrganizationType: null,
                      minutesDuration: 0,
                      startSection: '',
                      developmentSection: '',
                      closureSection: '',
                      valuationSection: '',
                      necessaryMaterials: resClass.necessaryMaterials.filter(mat => mat.necessaryMaterialType !== classNecessaryMaterialsTypes.teacher)
                    })
                    : resClass
                )
            })
            : res
        })
      }
    },
    setSavingResource(state, { saving, error }) {
      state.isSaving = saving
      state.savingError = error
    }
  },
  actions: {
    async createKreaResourceByContent({ commit }, { locale, contentId }) {
      commit('show', { element: 'fullPageLoader' })
      const response = await createResource(locale).catch(e => e.response)
      const successfulCreation = !isError(response.status)
      if (!successfulCreation) {
        commit('hide', { element: 'fullPageLoader' })
        return { success: successfulCreation }
      }
      const associationResponse = await associateResourceToEducationalContent({
        resourceId: response.data.id,
        locale,
        id: contentId
      }).catch(e => e.response)
      const successfulAssociation = !isError(associationResponse.status)
      commit('hide', { element: 'fullPageLoader' })
      return { success: successfulAssociation, id: response.data.id }
    },
    async hydrateKreaResource({ commit }, { id, locale }) {
      commit('show', { element: 'fullPageLoader' })
      const response = await getResource({ id, locale }).catch(e => e.response)
      const success = !isError(response.status)
      if (success) commit('hydrateKreaResource', response.data)
      commit('hide', { element: 'fullPageLoader' })
      return success
    },
    async updateKreaResource({ commit, state, rootState }) {
      commit('setSavingResource', { saving: true, error: false })
      const response = await editResource({
        locale: state.activeLanguage,
        id: state.resource.id,
        data: {
          ...state.resource,
          modifiedBy: rootState.userInfo.userInfo.userId,
          customKreaFieldOptionIds: state.resource.customKreaFieldOptions
            ? state.resource.customKreaFieldOptions.filter(op => op.id).map(op => op.id)
            : null
        }
      }).catch(e => e.response)
      const success = !isError(response.status)
      commit('setSavingResource', { saving: false, error: !success })
      return success
    },
    async deleteKreaResource({ commit }, { id, locale }) {
      const response = await deleteResource({ id, locale }).catch(e => e.response)
      const success = !isError(response.status)
      return success
    },
    async addLanguageKreaResource({ commit }, { id, locale }) {
      const response = await addLanguageResource({ id, locale }).catch(e => e.response)
      const success = !isError(response.status)
      return success
    },
    async suggestNewTopic({ commit }, topic) {
      const response = await suggestTopic(topic).catch(e => e.response)
      const success = !isError(response.status)
      return success
    },
    debounceKreaResourceUpdate: debounce(({ dispatch }) => {
      dispatch('updateKreaResource')
    }, 1500),
    setKreaActiveResourceLanguage({ commit }, locale) {
      commit('setKreaActiveResourceLanguage', locale)
    },
    clearKreaResource({ commit }) {
      commit('clearKreaResource')
    },
    editKreaLocalizedResourceField({ commit, dispatch }, { field, value, locale }) {
      commit('editKreaLocalizedResourceField', { field, value, locale })
      dispatch('debounceKreaResourceUpdate')
    },
    editKreaResourceField({ commit, dispatch }, { field, value }) {
      commit('editKreaResourceField', { field, value })
      dispatch('debounceKreaResourceUpdate')
    },
    editKreaCustomResourceField({ commit, dispatch }, { fieldId, newOptions }) {
      commit('editKreaCustomResourceField', { fieldId, newOptions })
      dispatch('debounceKreaResourceUpdate')
    },
    editKreaResourceClassField({ commit, dispatch }, { field, value, classNumber, locale }) {
      commit('editKreaResourceClassField', { field, value, classNumber, locale })
      dispatch('debounceKreaResourceUpdate')
    },
    addKreaResourceClassMaterial({ commit, dispatch }, { newMaterial, classNumber, locale }) {
      commit('addKreaResourceClassMaterial', { newMaterial, classNumber, locale })
      dispatch('debounceKreaResourceUpdate')
    },
    removeKreaResourceClassMaterial({ commit, dispatch }, { materialId, classNumber, locale }) {
      commit('removeKreaResourceClassMaterial', { materialId, classNumber, locale })
      dispatch('debounceKreaResourceUpdate')
    },
    addKreaResourceValuationMaterial({ commit, dispatch }, { newMaterial, locale }) {
      commit('addKreaResourceValuationMaterial', { newMaterial, locale })
      dispatch('debounceKreaResourceUpdate')
    },
    removeKreaResourceValuationMaterial({ commit, dispatch }, { materialId, locale }) {
      commit('removeKreaResourceValuationMaterial', { materialId, locale })
      dispatch('debounceKreaResourceUpdate')
    },
    addKreaResourceClass({ commit, dispatch }, locale) {
      commit('addKreaResourceClass', locale)
      dispatch('debounceKreaResourceUpdate')
    },
    removeKreaResourceClass({ commit, dispatch }, { locale, classNumber }) {
      commit('removeKreaResourceClass', { locale, classNumber })
      dispatch('debounceKreaResourceUpdate')
    },
    clearKreaStudentClass({ commit, dispatch }, { locale, classNumber }) {
      commit('clearKreaStudentClass', { locale, classNumber })
      dispatch('debounceKreaResourceUpdate')
    },
    clearKreaTeacherClass({ commit, dispatch }, { locale, classNumber }) {
      commit('clearKreaTeacherClass', { locale, classNumber })
      dispatch('debounceKreaResourceUpdate')
    }
  }
}
